/**
 * Hook for word cloud layout computation on the main thread
 *
 * Runs the cloud layout algorithm synchronously on the main thread.
 * For large word clouds, this may cause brief UI blocking.
 *
 * Features:
 * - Automatic font loading from stylesheets or Google Fonts
 * - Cancellation of in-flight computations
 * - Uses the CloudLayout engine with mask support
 */

import { useRef, useCallback, useEffect } from 'react'

import { CloudLayout } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/CloudLayout'

import * as d3 from 'd3'

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create a scale function from serialized parameters
 * @param {string} type
 * @param {[number, number]} domain
 * @param {[number, number]} range
 * @returns {(v: number) => number}
 */
export function createFontSizeScale(type, domain, range) {
  const [minTimes, maxTimes] = domain
  const [rangeStart, rangeEnd] = range

  if (minTimes === maxTimes) {
    return () => rangeEnd
  }

  if (type.startsWith('pow')) {
    const exponent = Number(type.slice(3)) / 10
    return d3.scalePow().exponent(exponent).domain([minTimes, maxTimes]).range([rangeStart, rangeEnd])
  }

  const scaleMap = {
    log: d3.scaleLog,
    linear: d3.scaleLinear,
    sqrt: d3.scaleSqrt
  }

  const scale = scaleMap[type] || d3.scaleSqrt
  return scale().domain([minTimes, maxTimes]).range([rangeStart, rangeEnd])
}

/**
 * Load a font from an ArrayBuffer into the document fonts
 * @param {string} fontFamily
 * @param {ArrayBuffer|null} fontData
 * @returns {Promise<boolean>}
 */
async function loadFont(fontFamily, fontData) {
  if (!fontData) return false
  if (typeof FontFace === 'undefined' || !document.fonts) return false

  try {
    const face = new FontFace(fontFamily, fontData)
    document.fonts.add(face)
    await face.load()
    return true
  } catch {
    return false
  }
}

/**
 * Verify the requested font is actually active by comparing
 * metrics against a known fallback
 * @param {string} fontFamily
 * @returns {boolean}
 */
function verifyFontAvailable(fontFamily) {
  const canvas = document.createElement('canvas')
  canvas.width = 100
  canvas.height = 100
  const ctx = canvas.getContext('2d')
  if (!ctx) return false

  ctx.font = '40px sans-serif'
  const w1 = ctx.measureText('Xqpg|MWIJ').width
  ctx.font = `40px ${fontFamily}`
  const w2 = ctx.measureText('Xqpg|MWIJ').width
  return w1 !== w2
}

/**
 * Find font URL in same-origin stylesheets
 * @param {string} fontFamily
 * @returns {string|null}
 */
function findFontUrlInStyleSheets(fontFamily) {
  const normalizedFamily = fontFamily.toLowerCase()

  for (let i = 0; i < document.styleSheets.length; i++) {
    try {
      const rules = document.styleSheets[i].cssRules || document.styleSheets[i].rules
      if (!rules) continue

      for (let j = 0; j < rules.length; j++) {
        if (rules[j] instanceof CSSFontFaceRule) {
          const family = rules[j].style.getPropertyValue('font-family').replace(/['"]/g, '').trim().toLowerCase()

          if (family === normalizedFamily) {
            const src = rules[j].style.getPropertyValue('src')
            const match = src.match(/url\(['"]?([^'")\s]+)['"]?\)/)

            if (match) {
              let fontUrl = match[1]
              if (fontUrl.startsWith('//')) fontUrl = location.protocol + fontUrl
              else if (fontUrl.startsWith('/')) fontUrl = location.origin + fontUrl
              return fontUrl
            }
          }
        }
      }
    } catch {
      // SecurityError for cross-origin sheets — expected, skip
    }
  }

  return null
}

/**
 * Find font URL from Google Fonts by re-fetching CSS
 * @param {string} fontFamily
 * @returns {Promise<string|null>}
 */
async function findFontUrlFromGoogleFonts(fontFamily) {
  const links = document.querySelectorAll('link[rel="stylesheet"][href*="fonts.googleapis.com"]')
  const encodedName = fontFamily.replace(/ /g, '+')
  const normalizedFamily = fontFamily.toLowerCase()

  for (let i = 0; i < links.length; i++) {
    const link = links[i]
    if (!link.href.includes(encodedName)) continue

    try {
      const response = await fetch(link.href)
      const css = await response.text()

      const blocks = css.split('@font-face')
      let latinUrl = null
      let fallbackUrl = null

      for (let b = 1; b < blocks.length; b++) {
        const block = blocks[b]
        const familyMatch = block.match(/font-family:\s*['"]?([^;'"]+)['"]?\s*;/)
        if (!familyMatch) continue
        if (familyMatch[1].trim().toLowerCase() !== normalizedFamily) continue

        let urlMatch = block.match(/url\(([^)]+)\)\s*format\(['"]woff2['"]\)/)
        if (!urlMatch) {
          urlMatch = block.match(/url\(([^)]+)\)/)
        }
        if (!urlMatch) continue

        const url = urlMatch[1].replace(/['"]/g, '')

        const rangeMatch = block.match(/unicode-range:\s*([^;]+);/)
        if (rangeMatch) {
          const range = rangeMatch[1]
          if (range.includes('U+0000-00FF') || range.includes('U+0041') || range.includes('U+0061')) {
            latinUrl = url
          }
        }

        fallbackUrl = url
      }

      return latinUrl || fallbackUrl
    } catch {
      return null
    }
  }

  return null
}

/**
 * Fetch URL as ArrayBuffer
 * @param {string} url
 * @returns {Promise<ArrayBuffer|null>}
 */
async function fetchAsArrayBuffer(url) {
  try {
    const response = await fetch(url)
    if (!response.ok) return null
    return await response.arrayBuffer()
  } catch {
    return null
  }
}

/**
 * Resolve font binary data for a font family.
 * No in-memory cache — the browser's HTTP cache already dedupes repeat fetches,
 * and an ArrayBuffer-per-font map would permanently retain several MB per page.
 * @param {string} font
 * @returns {Promise<ArrayBuffer|null>}
 */
export async function resolveFontData(font) {
  const url = findFontUrlInStyleSheets(font)
  if (url) return fetchAsArrayBuffer(url)

  const googleUrl = await findFontUrlFromGoogleFonts(font)
  if (googleUrl) return fetchAsArrayBuffer(googleUrl)

  return null
}

// =============================================================================
// React Hook
// =============================================================================

/**
 * Check if main-thread layout is supported
 * @returns {boolean}
 */
export function isWorkerSupported() {
  return typeof document !== 'undefined' && typeof FontFace !== 'undefined'
}

// A cancelled run was superseded -- by a newer request or by teardown. Callers must be able to
// tell it apart from a genuine failure, which is the only case a main-thread retry is right for.
export const LAYOUT_CANCELLED = 'layout-cancelled'

const cancelledError = () => Object.assign(new Error('Layout computation cancelled'), { code: LAYOUT_CANCELLED })

/**
 * Hook for managing cloud layout computation on the main thread
 * @returns {{ cancel: () => void, computeLayout: (config: Object) => Promise<Object> }}
 */
export function useCloudLayoutWorker() {
  const pendingRef = useRef(null)
  const requestCounterRef = useRef(0)
  const canvasRef = useRef(null)

  const cancel = useCallback(() => {
    if (pendingRef.current) {
      pendingRef.current.cancelled = true
      pendingRef.current = null
    }
  }, [])

  const computeLayout = useCallback(
    async config => {
      cancel()

      const requestId = ++requestCounterRef.current
      const pending = { cancelled: false, requestId }
      pendingRef.current = pending

      // Resolve font data
      let fontData = null
      try {
        fontData = await resolveFontData(config.font)
      } catch {
        // Continue without font data
      }

      if (pending.cancelled) {
        throw cancelledError()
      }

      // Load font into document context
      const fontLoaded = await loadFont(config.font, fontData)

      if (fontData && !fontLoaded && !verifyFontAvailable(config.font)) {
        // Font not available, continue with fallback rendering
      }

      if (pending.cancelled) {
        throw cancelledError()
      }

      // Create canvas for sprite rasterization
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas')
        canvasRef.current.width = 2048
        canvasRef.current.height = 2048
      }

      const fontSizeScale = createFontSizeScale(
        config.scaleFunction,
        [config.minTimes, config.maxTimes],
        [config.rangeStart, config.rangeEnd]
      )

      let angleCounter = 0
      const angles = config.angles
      const mergeField = config.mergeField

      // Create and configure layout engine
      const layout = new CloudLayout()
        .setCanvas(canvasRef.current)
        .setRatio(1)
        .setSize(config.size)
        .setWords(config.words)
        .setSpiral('rectangular')
        .setPadding(config.padding || 1)
        .setRotate(() => (0 < angles.length ? angles[angleCounter++ % angles.length] : 0))
        .setFont(config.font)
        .setFontSize(d => fontSizeScale(d[mergeField]))
        .randIndex(config.randIndex)
        .setBringWordsCloser(config.bringWordsCloser)
        .setBoundedRuntime(Boolean(config.boundedRuntime))

      if (config.mask32 && config.mask32.length > 0 && config.maskBound && config.maskBound.length === 4) {
        layout.setMask(config.mask32, config.maskBound)
      }

      // Run synchronous layout computation
      const result = layout.start()

      if (pending.cancelled) {
        throw cancelledError()
      }

      pendingRef.current = null

      return {
        tags: result ? result.tags : [],
        bounds: result ? result.bounds : null
      }
    },
    [cancel]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel()
      if (canvasRef.current) {
        // Force release of the ~16 MB backing store before GC runs
        canvasRef.current.width = 0
        canvasRef.current.height = 0
        canvasRef.current = null
      }
    }
  }, [cancel])

  return {
    cancel,
    computeLayout
  }
}

export default useCloudLayoutWorker
